// What waits in the Inbox (QA-344): work to book (sold estimates, membership visits due and open leads), jobs
// awaiting review, customers' replies no one has opened (gap #3), and the person's own tasks due today or
// before (BL-004), and the calls still to call back (BL-132, the Inbox's Calls tab), each read only for
// the roles that open that place. App re-reads them when the stream says a job, visit, estimate,
// membership, lead or call changed, a reply came in or replies were read; Nav counts them on Inbox and
// its places. Leads, jobs to review, replies and tasks are counted by the server (each action's `total`),
// never from a page of them. The new price book pictures waiting for approval (BL-122) count on Setup ›
// Price book for the owner and managers, read again when an answer there says so (PICTURES_CHANGED).

import { useCallback, useEffect, useState } from 'react';
import { client } from '../api.ts';
import { today as todayIn } from '../book-model.ts';
import { dueHorizon } from '../tobook/tobook-model.ts';
import { LOOK_BACK } from '../calls/callbacks-model.ts';
import { PICTURES_CHANGED } from '../settings/pictures-model.ts';

export type InboxCounts = {
  tobook?: number;
  review?: number;
  messages?: number;
  tasks?: number;
  callbacks?: number;
  pictures?: number;
};

/**
 * The live events that can change a count. A call ringing (call.received) is App's own, the pop, so
 * it isn't one: what it changes in the queue comes as the call's next event.
 */
export const INBOX_EVENTS =
  /^((job|appointment|estimate|membership|recurring_service|lead|visit|visit_request|task)\.|call\.(?!received$)|message\.received$|messages\.read$)/;

/** The new price book pictures waiting for approval, counted by the server (BL-122). */
function readPictures(set: (f: (c: InboxCounts) => InboxCounts) => void): void {
  void client
    .call<{ total: number }>('pricebook.art.toApprove', { limit: 1 })
    .then((r) => set((c) => ({ ...c, pictures: r.total })))
    .catch(() => undefined);
}

export function useInboxCounts(
  can: {
    tobook: boolean;
    review: boolean;
    messages?: boolean;
    tasks?: boolean;
    callbacks?: boolean;
    pictures?: boolean;
  },
  timeZone: string,
): { counts: InboxCounts; reload: () => void } {
  const [counts, setCounts] = useState<InboxCounts>({});
  const reload = useCallback(() => {
    if (can.tobook)
      void Promise.all([
        client.call<{ estimates: unknown[] }>('estimates.soldNotBooked', {}),
        client.call<{ due: unknown[] }>('recurringServices.due', { asOf: dueHorizon(todayIn(timeZone)) }),
        // To book's people are leads.manage's (LEAD_PERMS), so its open leads count too.
        client.call<{ total: number }>('leads.list', { status: 'open', limit: 1 }),
        // customers asking to move or cancel a visit (F-08)
        client.call<{ requests: unknown[] }>('visitRequests.list', {}),
      ])
        .then(([s, d, l, v]) =>
          setCounts((c) => ({
            ...c,
            tobook: s.estimates.length + d.due.length + l.total + v.requests.length,
          })),
        )
        .catch(() => undefined);
    if (can.review)
      void client
        .call<{ total: number }>('jobs.reviewQueue', { limit: 1 })
        .then((r) => setCounts((c) => ({ ...c, review: r.total })))
        .catch(() => undefined);
    if (can.messages)
      void client
        .call<{ total: number }>('messages.list', { direction: 'in', unread: true, limit: 1 })
        .then((r) => setCounts((c) => ({ ...c, messages: r.total })))
        .catch(() => undefined);
    if (can.tasks)
      void client
        // the open ones due today or before, counted by the server (not a page of them)
        .call<{ total: number }>('tasks.list', { assignee: 'me', dueBy: todayIn(timeZone), limit: 1 })
        .then((r) => setCounts((c) => ({ ...c, tasks: r.total })))
        .catch(() => undefined);
    // the callback queue's own look back (calls.callbacks' 14 days), as the Calls tab counts it
    if (can.callbacks)
      void client
        .call<{ calls: unknown[] }>('calls.callbacks', { days: LOOK_BACK[0] })
        .then((r) => setCounts((c) => ({ ...c, callbacks: r.calls.length })))
        .catch(() => undefined);
    if (can.pictures) readPictures(setCounts);
    if (!can.tobook && !can.review && !can.messages && !can.tasks && !can.callbacks && !can.pictures)
      setCounts({});
  }, [can.tobook, can.review, can.messages, can.tasks, can.callbacks, can.pictures, timeZone]);
  useEffect(reload, [reload]);
  // an answer in Setup › Price book › Pictures › To approve: the count looks again
  useEffect(() => {
    if (!can.pictures) return;
    const again = () => readPictures(setCounts);
    window.addEventListener(PICTURES_CHANGED, again);
    return () => window.removeEventListener(PICTURES_CHANGED, again);
  }, [can.pictures]);
  return { counts, reload };
}
